/**
 * @-mentions in comments.
 *
 * A mention is written into the text as `@Full Name` and stored alongside it
 * on the comment (`comment.mentions`), so the person is known by id even if
 * two people share a name, and the text still reads naturally anywhere it's
 * copied — the clipboard, the markdown export, Teams.
 */
import type { Comment, FeedbackItem, Mention, UserInfo } from '../types';

/**
 * The @-word being typed at the caret, if any: `@` at the start or after
 * whitespace, then anything up to the caret that isn't whitespace or another
 * `@`. Names have spaces, so the query is just the first word — enough to
 * narrow the list.
 */
export const findMentionQuery = (
  text: string,
  caret: number,
): { start: number; query: string } | null => {
  const match = /(^|\s)@([^\s@]*)$/.exec(text.slice(0, caret));
  if (!match) return null;
  return { start: caret - match[2].length - 1, query: match[2] };
};

/** Team members whose name or email starts a word with the query. */
export const matchMentionCandidates = <T extends Pick<UserInfo, 'name' | 'email'>>(
  members: T[],
  query: string,
  limit = 6,
): T[] => {
  const q = query.toLowerCase();
  return members
    .filter((m) =>
      !q ||
      m.name.toLowerCase().split(/\s+/).some((word) => word.startsWith(q)) ||
      m.email.toLowerCase().startsWith(q),
    )
    .slice(0, limit);
};

/** Replace the `@query` at `start…caret` with `@Name `, returning the new caret. */
export const insertMention = (
  text: string,
  start: number,
  caret: number,
  name: string,
): { text: string; caret: number } => {
  const inserted = `@${name} `;
  return {
    text: text.slice(0, start) + inserted + text.slice(caret),
    caret: start + inserted.length,
  };
};

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** `@Name`, not followed by more of a word (so `@Sam` doesn't match `@Samantha`). */
const mentionPattern = (name: string) => new RegExp(`(^|[^\\w])@${escapeRegExp(name)}(?![\\w])`);

/**
 * Who the text mentions, out of the team. Resolved from the text rather than
 * from what was picked in the menu, so a mention deleted before sending isn't
 * notified and one typed out in full still counts.
 */
export const resolveMentions = (text: string, members: UserInfo[]): Mention[] => {
  const seen = new Set<string>();
  const found: Mention[] = [];
  for (const m of members) {
    if (!m.name || seen.has(m.id) || !mentionPattern(m.name).test(text)) continue;
    seen.add(m.id);
    found.push({ id: m.id, name: m.name, email: m.email });
  }
  return found;
};

export type CommentSegment = { text: string; mention?: Mention };

/** Split comment text into plain runs and mentions, for highlighting. */
export const splitMentions = (text: string, mentions: Mention[] = []): CommentSegment[] => {
  if (mentions.length === 0) return [{ text }];
  // Longest first, so `@Sam Smith` wins over `@Sam`.
  const byLength = [...mentions].sort((a, b) => b.name.length - a.name.length);
  const pattern = new RegExp(
    `@(${byLength.map((m) => escapeRegExp(m.name)).join('|')})(?![\\w])`,
    'g',
  );
  const segments: CommentSegment[] = [];
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    // Must start the text or follow a non-word character, as when resolved.
    if (index > 0 && /\w/.test(text[index - 1])) continue;
    if (index > last) segments.push({ text: text.slice(last, index) });
    segments.push({ text: match[0], mention: byLength.find((m) => m.name === match[1]) });
    last = index + match[0].length;
  }
  if (last < text.length) segments.push({ text: text.slice(last) });
  return segments;
};

export const commentMentions = (comment: Comment, userId: string): boolean =>
  !!comment.mentions?.some((m) => m.id === userId);

/** Whether anyone tagged this user in any of the ticket's comments. */
export const ticketMentions = (item: FeedbackItem, userId: string): boolean =>
  !!item.comments?.some((c) => commentMentions(c, userId));
